/**
 * The Wallet app shell — a DROP-IN REPLACEMENT for
 * `components/layout/DashboardLayout.tsx`.
 *
 * It is deliberately the same SHAPE as the layout it stands in for: it reads
 * the same `useAuth()` state, mounts the same `<VisiblePagesProvider>`, shows
 * the same admin-scope banner and degraded-mode notice, and renders the same
 * `<Outlet/>` (through `<ScreenSlot/>`) over the same route table. Everything
 * that differs is CHROME.
 *
 * That symmetry is the whole safety argument for this shell:
 *
 *   - it adds NO route, so `routes.tsx` still generates every path from
 *     `PAGE_REGISTRY` and `tests/parity.test.ts` still passes untouched;
 *   - it is mounted INSIDE `<RequireAuth>`, so `guardRoute` has already
 *     decided the pathname before a single pixel of this file renders;
 *   - every link it draws is classified by `nav.ts` as registry-backed,
 *     admin-prefixed or one of the known shell paths — and `unlinkableHrefs()`
 *     throws in development if that ever stops being true.
 *
 * The one piece of state the shell OWNS is the header context — which period
 * and which account the screens below should be answering for. See
 * `context.tsx` for why the frame owns it rather than each screen.
 */
import { useCallback, useEffect, useMemo, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { useAuth } from "@/lib/auth/AuthProvider";
import type { PageKey } from "@/lib/rbac/pages";
import { VisiblePagesProvider } from "@/components/layout/VisiblePages";
import { AdminScopeBanner } from "@/components/layout/AdminScopeToggle";
import { NewVersionBanner } from "@/components/layout/NewVersionBanner";
import { apiFetch } from "@/lib/api/client";
import { apiUrl } from "@/lib/api/config";
import {
  WalletShellProvider,
  useWalletShell,
  type EntryType,
  type WalletAccountOption,
} from "./context";
import { WalletHeader } from "./WalletHeader";
import { BottomNav } from "./BottomNav";
import { MoreSheet } from "./MoreSheet";
import { SearchSheet } from "./SearchSheet";
import { ContextSheet } from "./ContextSheet";
import { EntryOverlay } from "./EntryOverlay";
import { ScreenSlot } from "./ScreenSlot";
import {
  DesktopBreadcrumb,
  DesktopSidebar,
  DesktopTopBar,
  useDesktopModules,
  useNewRecordShortcuts,
  useSidebarCollapsed,
} from "./DesktopFrame";
import { BreadcrumbTailProvider } from "./crumbs";
import { DesktopPageHead } from "./DesktopPageHead";
import { ENTRY_PAGE_KEYS, unlinkableHrefs } from "./nav";

/**
 * Loaded once for the whole shell rather than per screen, and published on the
 * context — the header needs the names for its chip and every screen needs the
 * same list, so fetching it four times would be four round-trips for one
 * answer. Unscoped on purpose: admins default to their OWN data (the
 * `effectiveScope` rule), and the account chip is a personal filter, not an
 * admin report.
 */
function useAccounts(enabled: boolean): WalletAccountOption[] {
  const { data } = useQuery({
    queryKey: ["wallet-shell-accounts"],
    enabled,
    staleTime: 5 * 60_000,
    queryFn: async (): Promise<WalletAccountOption[]> => {
      const res = await apiFetch(apiUrl("accounts", "", { includeArchived: false }));
      if (!res.ok) throw new Error("Failed to load accounts");
      // Anything but a list (an error body, a proxy page) must not take down
      // every screen: the shell renders its chrome around every page, so a
      // throw here blanks the whole app.
      const body: unknown = await res.json().catch(() => null);
      return Array.isArray(body) ? (body as WalletAccountOption[]) : [];
    },
  });
  return Array.isArray(data) ? data : [];
}

/** The chrome. Split out so it can sit INSIDE `WalletShellProvider`. */
function ShellFrame({
  user,
  visible,
  degradedMessage,
}: {
  user: { name: string; role: string };
  visible: ReadonlySet<PageKey>;
  degradedMessage: string | null;
}) {
  const { openEntry, entryOpen, searchOpen, openSearch, closeSearch } = useWalletShell();
  const [moreOpen, setMoreOpen] = useState(false);
  const [contextOpen, setContextOpen] = useState(false);

  const isAdmin = user.role === "ADMIN";
  const canAdd = ENTRY_PAGE_KEYS.some((key) => visible.has(key));
  const modules = useDesktopModules(visible, isAdmin);
  const [collapsed, setCollapsed] = useSidebarCollapsed();
  // E / I / T (N) open New Record from any page on desktop.
  const addFromKey = useCallback((type: EntryType) => openEntry(type), [openEntry]);
  useNewRecordShortcuts(canAdd && !entryOpen && !searchOpen, addFromKey);

  return (
    <div
      className="wd wd-frame min-h-screen bg-[var(--cp-canvas)] text-[var(--cp-ink)]"
      data-side={collapsed ? "collapsed" : undefined}
    >
      {/* `lg`+ : the MDM-style frame (top bar, module sidebar, breadcrumb)
          replaces BOTH the phone header and the bottom bar. Below `lg` the
          phone chrome is exactly as before. */}
      <DesktopTopBar user={user} modules={modules} onOpenSearch={() => openSearch()} />
      <DesktopSidebar
        modules={modules}
        canAdd={canAdd}
        onAdd={(type) => openEntry(type)}
        collapsed={collapsed}
        onCollapse={setCollapsed}
      />

      <div className="wd-content min-w-0 flex-1">
        <div className="lg:hidden">
          <WalletHeader
            user={user}
            onOpenContext={() => setContextOpen(true)}
            onOpenSearch={() => openSearch()}
          />
        </div>

        <main className="wd-main mx-auto max-w-5xl lg:mx-0 lg:max-w-none">
          <DesktopBreadcrumb modules={modules} />
          <AdminScopeBanner />
          {degradedMessage && (
            <div className="mx-4 mt-4 rounded-[var(--cp-r-control)] border border-[var(--cp-amber)] bg-[var(--cp-amber-wash)] px-3 py-2 text-xs text-[var(--cp-amber-ink)] lg:mx-0">
              {degradedMessage}
            </div>
          )}
          <DesktopPageHead onOpenContext={() => setContextOpen(true)} />
          {/* Clear the 60px bar plus the home indicator on phones only. */}
          <div className="p-4 pb-[calc(60px+1.5rem+env(safe-area-inset-bottom))] lg:p-0">
            <ScreenSlot />
          </div>
        </main>
      </div>

      <div className="lg:hidden">
        <BottomNav
          visible={visible}
          canAdd={canAdd}
          onAdd={() => openEntry("expense")}
          onMore={() => setMoreOpen(true)}
          moreOpen={moreOpen}
        />
      </div>

      <MoreSheet
        open={moreOpen}
        onClose={() => setMoreOpen(false)}
        visible={visible}
        isAdmin={isAdmin}
      />
      <ContextSheet open={contextOpen} onClose={() => setContextOpen(false)} />
      <SearchSheet open={searchOpen} onClose={closeSearch} />
      {/* Mounted unconditionally; it renders nothing until the shell's
          `entryOpen` flips. Keeping it mounted is what lets the sheet play its
          exit animation instead of vanishing. */}
      <EntryOverlay />
      {/* After a portal deploy: "A new version of AIM is ready — Reload". */}
      <NewVersionBanner />
    </div>
  );
}

export function WalletShell() {
  const { user, visiblePageKeys, degradedMessage } = useAuth();
  const accounts = useAccounts(Boolean(user));

  // Fails loudly in dev if a navigation entry ever points at a path the
  // registry does not know — the one way this shell could weaken the
  // permission model. Never runs in a production build.
  useEffect(() => {
    if (!import.meta.env.DEV) return;
    const offenders = unlinkableHrefs();
    if (offenders.length > 0) {
      throw new Error(
        `WalletShell would link to unrouted path(s): ${offenders.join(", ")}. ` +
          "Add a PAGE_REGISTRY row (and its route element) before linking to it.",
      );
    }
  }, []);

  const visible = useMemo(() => new Set(visiblePageKeys), [visiblePageKeys]);

  // `RequireAuth` guarantees a user before this renders; the guard keeps
  // TypeScript honest rather than covering a reachable state. Same line as
  // `DashboardLayout`, for the same reason.
  if (!user) return null;

  return (
    <VisiblePagesProvider pageKeys={visiblePageKeys}>
      <WalletShellProvider accounts={accounts}>
        <BreadcrumbTailProvider>
          <ShellFrame
            user={{ name: user.name ?? "", role: user.role as string }}
            visible={visible}
            degradedMessage={degradedMessage}
          />
        </BreadcrumbTailProvider>
      </WalletShellProvider>
    </VisiblePagesProvider>
  );
}
